import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  TextField,
  Alert,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from "@mui/material";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL || process.env.REACT_APP_API_URL;

// --- Helper Functions ---

const fmtDate = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};

const hasContent = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "number") return true;
  if (typeof v === "boolean") return true;
  return true;
};

const styles = {
  section: {
    marginBottom: "15px",
  },
  checkboxGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "6px 20px",
  },
  checkboxRow: {
    display: "flex",
    alignItems: "center",
  },
};

const fmtMultiSelect = (arr, otherVal) => {
  let display = "";

  if (Array.isArray(arr) && arr.length > 0) {
    display = arr.join(", ");
  } else if (typeof arr === "string" && arr.trim().length > 0) {
    display = arr.trim();
  }

  if (typeof otherVal === "string" && otherVal.trim().length > 0) {
    display =
      display !== ""
        ? `${display} (Other: ${otherVal.trim()})`
        : `Other: ${otherVal.trim()}`;
  }

  return display;
};

const fmtBoolOrStr = (val) => {
  if (val === true) return "Yes";
  if (val === false) return "No";
  return val;
};

const BaselinePreview = ({ patientformID, patientFormId, data }) => {
  const [form, setForm] = useState(null);    // baseline questionnaire
  const [header, setHeader] = useState(null); // Forms table row
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const resolvedPatientFormId = useMemo(
    () =>
      patientformID ??
      patientFormId ??
      data?.patientformID ??
      data?.patientFormId ??
      data?.id ??
      data?.Id ??
      null,
    [patientformID, patientFormId, data]
  );

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    const run = async () => {
      if (!resolvedPatientFormId) {
        setError("Missing patientFormId");
        setLoading(false);
        return;
      }
      if (!API_BASE_URL) {
        setError("API base URL is not set.");
        setLoading(false);
        return;
      }
      const token = localStorage.getItem(
        "neuro_patientportal_auth_token"
      );
      if (!token) {
        setError("Not authenticated (missing token)");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const headersCfg = { Authorization: `Bearer ${token}` };

        const [formDataRes, headerRes] = await Promise.all([
          axios.get(
            `${API_BASE_URL}/BaseLineQuestionnaires/BaseLineQuestionnaireByFormId`,
            {
              params: { patientFormId: resolvedPatientFormId },
              headers: headersCfg,
              signal: controller.signal,
              validateStatus: (s) =>
                (s >= 200 && s < 300) || s === 204 || s === 404,
            }
          ),
          axios.get(`${API_BASE_URL}/Forms/GetPatientFormsbyId`, {
            params: { Id: resolvedPatientFormId },
            headers: headersCfg,
            signal: controller.signal,
            validateStatus: (s) =>
              (s >= 200 && s < 300) || s === 204 || s === 404,
          }),
        ]);

        if (!active) return;

        const formData =
          formDataRes.status === 204 || formDataRes.status === 404
            ? null
            : formDataRes.data;

        const headerRaw =
          headerRes.status === 204 || headerRes.status === 404
            ? null
            : headerRes.data;

        const headerData = Array.isArray(headerRaw)
          ? headerRaw[0] || {}
          : headerRaw || null;

        setForm(formData);
        setHeader(headerData);

        if (!formData && !headerData) {
          setError(
            "No Baseline Questionnaire found for this patient."
          );
        }
      } catch (err) {
        if (!active) return;
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load form data."
        );
      } finally {
        if (active) setLoading(false);
      }
    };

    run();
    return () => {
      active = false;
      controller.abort();
    };
  }, [resolvedPatientFormId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Alert severity="warning">{error}</Alert>
      </Box>
    );
  }

  if (!form && !header) {
    return (
      <Box p={4}>
        <Typography>No data available.</Typography>
      </Box>
    );
  }

  // --- HEADER & METADATA ---
  const assessmentDate =
    form?.assessmentDate ||
    header?.assessmentDate ||
    header?.createdDate;
  const clientName =
    header?.patientName || form?.patientName || "";
  const fileId =
    header?.fileNo ||
    header?.fileno ||
    header?.accountNo ||
    form?.fileId ||
    "";
  const createdBy =
    form?.createdBy || header?.createdBy || "";
  const titleField =
    form?.customFormTitle ||
    header?.customFormTitle ||
    "Baseline Questionnaire";

  const ageValue =
    form?.age ?? header?.patientAge ?? header?.age ?? "";

  // ---------- CHECKBOX OPTIONS ----------
  const raceOptions = [
    "Black or African American",
    "Other race",
    "Decline to answer",
  ];
  const ethnicityOptions = ["Somali", "Other"];
  const homeLanguageOptions = [
    "English",
    "Swahili",
    "Somali",
    "Arabic",
    "Other",
  ];
  const healthConditionOptions = [
    "High blood pressure",
    "Diabetes",
    "Anemia",
    "Depression or anxiety",
    "None",
    "Other",
  ];

  // Normalize stored values to arrays for checkbox rendering
  const toArray = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === "string" && val.trim()) {
      return val
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
  };

  const raceValues = toArray(form?.race);
  const ethnicityValues = toArray(form?.ethnicity);
  const homeLanguageValues = toArray(form?.homeLanguage);
  const healthConditionValues = toArray(form?.healthConditions);

  // --- SECTION 1: DEMOGRAPHICS / BACKGROUND (non-checkbox fields) ---
  const sectionOneFields = [
    [
      "Which best describes your living situation?",
      form?.livingSituation ?? "",
    ],
    ["How old are you?", ageValue],
    [
      "Marital / Relationship status",
      form?.maritalStatus ?? "",
    ],
    [
      "Do you currently live with your spouse?",
      form?.liveWithSpouse ?? "",
    ],
    [
      "How many people live in your household?",
      form?.householdSize ?? "",
    ],
    ["Where were you born?", form?.birthplace ?? ""],
    [
      "If born in other country, please specify",
      form?.birthplaceOther ?? "",
    ],
    [
      "How long have you lived in the United States?",
      form?.yearsInUS ?? "",
    ],
    [
      "Highest level of education completed",
      form?.education ?? "",
    ],
    [
      "Do you have health insurance?",
      form?.healthInsurance ?? "",
    ],
    [
      "Do you feel safe in your home?",
      form?.feelSafe ?? "",
    ],
    [
      "Do you currently use tobacco (cigarettes, cigars, chewing tobacco, e-cigarettes)?",
      form?.tobaccoUse ?? "",
    ],
    [
      "Do you have a history of substance abuse/treatment?",
      form?.substanceHistory ?? "",
    ],
    ["Are you currently working?", form?.employment ?? ""],
    [
      "Monthly income range",
      form?.monthlyIncome ?? "",
    ],
    [
      "How many people in your household live on that monthly income?",
      form?.incomeDependents ?? "",
    ],
  ];

  // --- SECTION 2: HEALTH ---
  const sectionTwoFields = [
    ["Weeks Pregnant", form?.pregnancyWeeks ?? ""],
    [
      "Miscarriages/Stillbirths",
      fmtBoolOrStr(form?.miscarriages) ?? "",
    ],
    [
      "Health Conditions",
      fmtMultiSelect(
        form?.healthConditions,
        form?.healthConditionOther
      ),
    ],
    [
      "Has Regular Doctor",
      fmtBoolOrStr(form?.regularDoctor) ?? "",
    ],
    [
      "Seen Doctor this Pregnancy",
      fmtBoolOrStr(form?.seenDoctor) ?? "",
    ],
    ["Reason for No Doctor", form?.reasonNoDoctor ?? ""],
    [
      "Reason for Seeking Prenatal Care",
      form?.reasonPrenatalCare ?? "",
    ],
  ];

  // --- SECTION 3: SOCIAL SUPPORT ---
  const sectionThreeFields = [
    [
      "Community/Religious Groups",
      form?.communityGroups ?? "",
    ],
    [
      "Connected to Other Women/Mothers",
      form?.connectedWomenMothers ?? "",
    ],
    [
      "Emotional Support Frequency",
      form?.familyOrFriendsEmotional ?? "",
    ],
    [
      "Other Support Frequency",
      form?.familyOrFriendsSupport ?? "",
    ],
    [
      "Support Felt Missing",
      form?.supportYouFeel ?? "",
    ],
    [
      "Has Someone to Talk to",
      form?.feelLikeSomeone ?? "",
    ],
    [
      "Help with Prenatal Care",
      form?.prenatalCareTransportation ?? "",
    ],
  ];

  // --- SECTION 4: KNOWLEDGE ---
  const sectionFourFields = [
    [
      "Knows 1st Trimester Frequency",
      fmtBoolOrStr(
        form?.firstPregnantWomenDoctor
      ) ?? "",
    ],
    [
      "1st Trimester Frequency Answer",
      form?.pregnantWomenDoctorTrimester ?? "",
    ],
    [
      "Knows 2nd Trimester Frequency",
      fmtBoolOrStr(
        form?.secondPregnantWomenDoctor
      ) ?? "",
    ],
    [
      "2nd Trimester Frequency Answer",
      form?.secondMidwifePregnantWomenDoctor ??
      "",
    ],
    [
      "Knows 3rd Trimester Frequency",
      fmtBoolOrStr(
        form?.thirdTrimesterPregnantWomenDoctor
      ) ?? "",
    ],
    [
      "3rd Trimester Frequency Answer",
      form?.fourthPregnantWomenDoctor ?? "",
    ],
    [
      "Why Appointments Important",
      form?.regularPrenatalAppointmentsImportant ??
      "",
    ],
    [
      "Recommended During Pregnancy",
      form?.recommendedDuringPregnancy ?? "",
    ],
    [
      "Avoid During Pregnancy",
      form?.avoidDuringPregnancy ?? "",
    ],
    [
      "Warning Symptoms (Pregnancy)",
      form?.symptomsDuringPregnancy ?? "",
    ],
    [
      "Warning Symptoms (Postpartum)",
      form?.symptomsDuringPostpartum ?? "",
    ],
  ];

  // --- SECTION 5: GOALS ---
  const sectionFiveFields = [
    ["Pregnancy Goal", form?.goalPregnancy ?? ""],
    ["Delivery/Childbirth Goal", form?.goalDelivery ?? ""],
    ["Postpartum Goal", form?.goalPostpartum ?? ""],
  ];

  return (
    <Paper sx={{ p: 0, borderRadius: 3, mb: 4 }}>
      {/* STICKY HEADER */}
      <Box
        sx={{
          position: "sticky",
          top: "-16px",
          zIndex: (t) => t.zIndex.appBar,
          bgcolor: "#fff",
          p: 4,
          pb: 0,
          mb: 3,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ position: "absolute", top: 16, left: 16 }}
        >
          <strong>Assessment Date:</strong>{" "}
          {fmtDate(assessmentDate)}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            fontWeight: "bold",
          }}
        >
          Baseline Questionnaire
        </Typography>
        <Box sx={{ height: 28 }} />
        <Divider />
      </Box>

      {/* PATIENT STRIP */}
      <Box sx={{ px: 4 }}>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          {hasContent(fileId) && (
            <Grid item xs={12} md={4}>
              <Typography variant="body2">
                <strong>File #:</strong> {fileId}
              </Typography>
            </Grid>
          )}
          {hasContent(clientName) && (
            <Grid item xs={12} md={4} textAlign="center">
              <Typography variant="body2">
                <strong>Client:</strong> {clientName}
              </Typography>
            </Grid>
          )}
          {hasContent(createdBy) && (
            <Grid item xs={12} md={4} textAlign="right">
              <Typography variant="body2">
                <strong>Created By:</strong> {createdBy}
              </Typography>
            </Grid>
          )}
        </Grid>
        <Divider sx={{ mb: 3 }} />
      </Box>

      {/* TITLE + ASSESSMENT DATE FIELDS */}
      <Box sx={{ px: 4 }}>
        <Grid
          container
          spacing={2}
          sx={{ mb: 2, alignItems: "center" }}
        >
          {hasContent(titleField) && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Title"
                value={titleField}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}
          {hasContent(assessmentDate) && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Assessment Date"
                value={fmtDate(assessmentDate)}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}
        </Grid>
      </Box>

      <Divider sx={{ my: 2, mx: 4 }} />

      {/* MAIN CONTENT */}
      <Box sx={{ px: 4, pb: 4 }}>
        {/* SECTION 1 */}
        <>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#356F60", fontWeight: "bold" }}
          >
            Section One: Demographic / Background Information
          </Typography>

          <Grid container spacing={2}>
            {/* RACE CHECKBOXES */}
            <Grid item xs={12} md={4}>
              <Typography>
                How do you identify your race?
              </Typography>
              <FormGroup>
                {raceOptions.map((opt) => (
                  <FormControlLabel
                    key={opt}
                    control={
                      <Checkbox
                        checked={raceValues.includes(opt)}
                        inputProps={{ readOnly: true }}
                      />
                    }
                    label={opt}
                  />
                ))}
              </FormGroup>
              {hasContent(form?.raceOther) && (
                <TextField
                  fullWidth
                  label="Specify other race"
                  value={form?.raceOther}
                  InputProps={{ readOnly: true }}
                  sx={{ mt: 1 }}
                />
              )}
            </Grid>

            {/* ETHNICITY CHECKBOXES */}
            <Grid item xs={12} md={4}>
              <Typography>
                How do you identify your ethnicity? (Select all
                that apply)
              </Typography>
              <FormGroup>
                {ethnicityOptions.map((opt) => (
                  <FormControlLabel
                    key={opt}
                    control={
                      <Checkbox
                        checked={ethnicityValues.includes(opt)}
                        inputProps={{ readOnly: true }}
                      />
                    }
                    label={opt}
                  />
                ))}
              </FormGroup>
              {hasContent(form?.ethnicityOther) && (
                <TextField
                  fullWidth
                  label="Specify other ethnicity"
                  value={form?.ethnicityOther}
                  InputProps={{ readOnly: true }}
                  sx={{ mt: 1 }}
                />
              )}
            </Grid>

            {/* HOME LANGUAGE CHECKBOXES */}
            <Grid item xs={12} md={4}>
              <Typography>
                Which language(s) do you primarily speak at home?
                (Select all)
              </Typography>
              <FormGroup>
                {homeLanguageOptions.map((opt) => (
                  <FormControlLabel
                    key={opt}
                    control={
                      <Checkbox
                        checked={homeLanguageValues.includes(
                          opt
                        )}
                        inputProps={{ readOnly: true }}
                      />
                    }
                    label={opt}
                  />
                ))}
              </FormGroup>
              {hasContent(form?.homeLanguageOther) && (
                <TextField
                  fullWidth
                  label="Specify other language"
                  value={form?.homeLanguageOther}
                  InputProps={{ readOnly: true }}
                  sx={{ mt: 1 }}
                />
              )}
            </Grid>

            {/* OTHER SECTION ONE FIELDS */}
            {sectionOneFields.map(([label, value], idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Typography>{label}</Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={1}
                  value={
                    hasContent(value) ? String(value) : ""
                  }
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 3 }} />
        </>

        {/* SECTION 2 */}
        {sectionTwoFields.length > 0 && (
          <>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#356F60", fontWeight: "bold" }}
            >
              Section Two: Basic Health Information
            </Typography>
            <Grid container spacing={2}>
              {sectionTwoFields.map(
                ([label, value], idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Typography>{label}</Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={1}
                      value={
                        hasContent(value)
                          ? String(value)
                          : ""
                      }
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                )
              )}
              <div style={styles.section}>
                <h4>
                  Do you have any of the following health
                  conditions?
                </h4>

                {healthConditionOptions.map(
                  (option, index) => (
                    <div
                      key={index}
                      style={styles.checkboxRow}
                    >
                      <input
                        type="checkbox"
                        checked={healthConditionValues.includes(
                          option
                        )}
                        readOnly
                      />
                      <span
                        style={{ marginLeft: "8px" }}
                      >
                        {option}
                      </span>
                    </div>
                  )
                )}

                {/* Show "Other" text if exists */}
                {healthConditionValues.includes(
                  "Other"
                ) &&
                  form?.healthConditionOther && (
                    <div style={{ marginTop: "5px" }}>
                      <strong>Other:</strong>{" "}
                      {form.healthConditionOther}
                    </div>
                  )}
              </div>
            </Grid>
            <Divider sx={{ my: 3 }} />
          </>
        )}

        {/* SECTION 3 */}
        {sectionThreeFields.length > 0 && (
          <>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#356F60", fontWeight: "bold" }}
            >
              Section Three: Community and Social Support
            </Typography>
            <Grid container spacing={2}>
              {sectionThreeFields.map(
                ([label, value], idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Typography>{label}</Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={1}
                      value={
                        hasContent(value)
                          ? String(value)
                          : ""
                      }
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                )
              )}
            </Grid>
            <Divider sx={{ my: 3 }} />
          </>
        )}

        {/* SECTION 4 */}
        {sectionFourFields.length > 0 && (
          <>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#356F60", fontWeight: "bold" }}
            >
              Section Four: Baseline Knowledge
            </Typography>
            <Grid container spacing={2}>
              {sectionFourFields.map(
                ([label, value], idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Typography>{label}</Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={1}
                      value={
                        hasContent(value)
                          ? String(value)
                          : ""
                      }
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                )
              )}
            </Grid>
            <Divider sx={{ my: 3 }} />
          </>
        )}

        {/* SECTION 5 */}
        {sectionFiveFields.length > 0 && (
          <>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#356F60", fontWeight: "bold" }}
            >
              Section Five: Personal Goals
            </Typography>
            <Grid container spacing={2}>
              {sectionFiveFields.map(
                ([label, value], idx) => (
                  <Grid item xs={12} key={idx}>
                    <Typography>{label}</Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      value={
                        hasContent(value)
                          ? String(value)
                          : ""
                      }
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                )
              )}
            </Grid>
          </>
        )}

        {!sectionOneFields.length &&
          !sectionTwoFields.length &&
          !sectionThreeFields.length &&
          !sectionFourFields.length &&
          !sectionFiveFields.length && (
            <Typography
              variant="body1"
              sx={{
                fontStyle: "italic",
                color: "gray",
                mt: 2,
              }}
            >
              No responses recorded for this questionnaire.
            </Typography>
          )}
      </Box>
    </Paper>
  );
};

export default BaselinePreview;